import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import JsBarcode from "jsbarcode";
import { Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCurrency } from "@/hooks/useCurrency";
import { isValidEan13 } from "@/lib/ean13";

export function ProductLabel({
	name,
	barcode,
	price,
	taxRate,
	weightBased,
	saved,
	variants = [],
}: {
	name: string;
	barcode: string;
	price: number;
	taxRate: number;
	weightBased: boolean;
	saved: boolean;
	variants?: { id?: string; name: string; sellingPrice: number }[];
}) {
	const { t, i18n } = useTranslation();
	const currency = useCurrency();
	const svg = useRef<SVGSVGElement>(null);
	const preview = useRef<HTMLDivElement>(null);
	const [variantId, setVariantId] = useState("");
	const selectedVariant = variants.find(v => v.id === variantId);
	const labelName = selectedVariant ? `${name} · ${selectedVariant.name}` : name;
	const labelPrice = selectedVariant?.sellingPrice ?? price;
	const [size, setSize] = useState("50x30");
	const [copies, setCopies] = useState(1);
	const [valid, setValid] = useState(false);
	const [printError, setPrintError] = useState(false);
	useEffect(() => {
		if (!svg.current) return;
		try {
			JsBarcode(svg.current, barcode, {
				format: isValidEan13(barcode) ? "EAN13" : "CODE128",
				displayValue: true,
				fontSize: 12,
				height: 36,
				margin: 8,
				width: 1.5,
			});
			setValid(true);
		} catch {
			svg.current.replaceChildren();
			setValid(false);
		}
	}, [barcode, weightBased]);
	const print = () => {
		const popup = window.open("", "_blank", "width=600,height=600");
		if (!popup || !preview.current) {
			setPrintError(true);
			return;
		}
		const [width, height] = size.split("x");
		popup.document.title = t("productSetup.printLabel");
		const style = popup.document.createElement("style");
		style.textContent = `@page{size:${width}mm ${height}mm;margin:0}body{margin:0;font:11px Arial,sans-serif;color:#000;background:#fff}.label{box-sizing:border-box;width:${width}mm;height:${height}mm;padding:2mm;display:flex;flex-direction:column;align-items:center;justify-content:center;overflow:hidden;break-after:page;text-align:center}.label:last-child{break-after:auto}.label svg{max-width:100%;height:14mm}.label strong{font-size:12px}.label p{margin:2px 0}`;
		popup.document.head.append(style);
		for (let i = 0; i < copies; i++) {
			const label = preview.current.cloneNode(true) as HTMLElement;
			label.className = "label";
			label.removeAttribute("style");
			popup.document.body.append(label);
		}
		popup.document.documentElement.dir = i18n.dir();
		popup.document.close();
		popup.focus();
		popup.addEventListener("afterprint", () => popup.close(), { once: true });
		setTimeout(() => popup.print(), 250);
		setPrintError(false);
	};
	return (
		<section className="rounded-2xl bg-card p-4 shadow-sm space-y-3">
			<h2 className="font-semibold flex items-center gap-2">
				<Printer className="h-5 w-5 text-muted-foreground" />
				{t("productSetup.printLabel")}
			</h2>
			{weightBased ? (
				<p className="text-sm text-muted-foreground">{t("productSetup.scalePrint")}</p>
			) : (
				<>
					<div className="space-y-3">
						{!!variants.length && (
							<>
								<Select value={variantId} onValueChange={setVariantId}>
									<SelectTrigger aria-label={t("productSetup.chooseVariant")}>
										<SelectValue placeholder={t("productSetup.chooseVariant")} />
									</SelectTrigger>
									<SelectContent>
										{variants
											.filter(v => v.id)
											.map(v => (
												<SelectItem key={v.id} value={v.id!}>
													{v.name}
												</SelectItem>
											))}
									</SelectContent>
								</Select>
								<p className="text-xs text-muted-foreground">{t("productSetup.variantLabel")}</p>
							</>
						)}
						<div className="flex flex-wrap items-center gap-4">
							<div
								ref={preview}
								className="rounded-lg border bg-white text-black p-3 text-center w-52 overflow-hidden"
							>
								<strong className="block text-sm">{labelName || t("products.form.name")}</strong>
								<p className="text-sm">
									{(labelPrice * (1 + taxRate / 100)).toFixed(2)} {currency}
								</p>
								<svg ref={svg} className="max-w-full h-16" role="img" aria-label={barcode} />
							</div>
							<div className="flex-1 min-w-40 space-y-3">
								<div className="grid grid-cols-2 gap-3">
									<div>
										<Label>{t("productSetup.labelSize")}</Label>
										<Select value={size} onValueChange={setSize}>
											<SelectTrigger aria-label={t("productSetup.labelSize")}>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{["40x25", "50x30", "58x40"].map(s => (
													<SelectItem value={s} key={s}>
														{s.replace("x", " × ")} mm
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
									<div>
										<Label htmlFor="label-copies">{t("productSetup.copies")}</Label>
										<Input
											id="label-copies"
											type="number"
											min="1"
											max="100"
											value={copies}
											onChange={e =>
												setCopies(
													Math.min(100, Math.max(1, Math.trunc(Number(e.target.value)) || 1)),
												)
											}
										/>
									</div>
								</div>
								<p className="text-xs text-muted-foreground">
									{t(saved ? "productSetup.labelTax" : "productSetup.saveBeforePrint")}
								</p>
								<Button
									variant="outline"
									onClick={print}
									disabled={
										!saved || !valid || !name.trim() || (!!variants.length && !selectedVariant)
									}
								>
									<Printer className="h-4 w-4 me-2" />
									{t("productSetup.printLabel")}
								</Button>
								{printError && (
									<p role="alert" className="text-sm text-destructive">
										{t("productSetup.popupBlocked")}
									</p>
								)}
							</div>
						</div>
					</div>
				</>
			)}
		</section>
	);
}
